import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import { ChipList } from '@progress/kendo-react-buttons';
import cn from 'clsx';
import { PanelPreview } from '../panel-preview/panel-preview';
import { IFormData, Options, SortByDate, YAxisData } from './interfaces';
import styles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { FieldDefinitionFieldTypeDto, RowDataSourceTableDto } from '@interfaces-clean/field-rdl-definition.interface';
import { usePanelDetailsContext } from '@pages-clean/admin/study/manage-panels/context';
import { COMPONENT_NAME, ICreateData } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/shared';
import PanelReasonForChangeDialog from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/panel-reason-for-change-dialog';
import {
    PanelInput,
    PanelType,
    RowDataSourceTag,
    StableVariableType,
    ThresholdCalculation,
} from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_PANEL, GET_PANELS, SAVE_PANEL } from '@queries/central-monitoring';
import {
    getPanels,
    panel_OpraQueries_panel_Panel,
    panel as IPanel,
    panelVariables as IPanelVariables,
    upsertPanel,
    upsertPanelVariables,
} from '@queries/generated/central-monitoring.generated';
import { useGetLatestDataSet } from '@apis/bff-api/central-monitoring';

interface ITriOpDateProps {
    panel?: panel_OpraQueries_panel_Panel;
    onCancelOverride?: () => void;
    onUpdate: () => void;
    onCreate: (panelRef: string) => void;
    canEdit?: boolean;
    processedConfig?: panel_OpraQueries_panel_Panel;
}

interface IProps {
    panelRef?: string;
    createData?: ICreateData;
    canEdit?: boolean;
}

enum ChipValues {
    Configuration = 1,
    Preview = 2,
}

const TRIOpDateConfig = ({
    panel,
    onCancelOverride,
    onUpdate,
    onCreate,
    canEdit,
    processedConfig,
}: ITriOpDateProps) => {
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.triOpDate' });
    const { t: tOptions } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.triOpDate.options' });
    const { t: tCommon } = useTranslation('common');
    const { t: tManagePanels } = useTranslation('admin', { keyPrefix: 'managePanels' });
    const [chip, setChip] = useState<number>(ChipValues.Configuration);
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const { source } = usePanelDetailsContext();

    const [showReasonForChange, setShowReasonForChange] = useState(false);

    const {
        SiteAverageOptions,
        DataInclusionPeriodOptions,
        DataPopulationOptions,
        DataInclusionUnits,
        ThresholdCalculationOptions,
        StableTypeOptions,
    } = Options(tOptions);

    const [savePanel, { loading: isSaving }] = useMutation<upsertPanel, upsertPanelVariables>(SAVE_PANEL);
    const { state } = useLocalState();

    const { data, loading: isLoading } = useQuery<getPanels>(GET_PANELS, {
        variables: {
            organisationId: state?.selectedOrganisation?.id,
            studyId: state?.selectedStudy?.id,
        },
        fetchPolicy: 'no-cache',
    });

    const studyPanels = useMemo(() => {
        return data?.panels?.flatMap((studyPanel) => {
            if (!!studyPanel?.plotTitle && studyPanel.id !== panel?.id) return [studyPanel?.plotTitle];
            return [];
        });
    }, [data]);

    const rowDataOptions = state?.cm2DataSources
        ?.flatMap((d) => {
            if (d?.id == null || d?.name == null || d.table == null) return [];

            if (!d?.tags?.includes(RowDataSourceTag.PANEL) || d?.default) return [];

            if (d.table === RowDataSourceTableDto.SITE) return [];

            return [
                {
                    id: d?.id,
                    name: d?.name,
                    table: d.table,
                },
            ];
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    const getFieldOptions = (table: string, fieldTypes: FieldDefinitionFieldTypeDto[]) => {
        return state?.cm2Fields
            ?.filter((f) => {
                if (f?.fieldType == null) return false;
                if (table !== f?.dataType) return false;
                if (f.isHidden) return false;

                return fieldTypes.includes(f?.fieldType);
            })
            .flatMap((f) => {
                if (f?.key == null || f.name == null) return [];

                return [
                    {
                        id: f?.key,
                        name: f.name,
                    },
                ];
            });
    };
    const getYAxisOptions = (table: string) =>
        getFieldOptions(table, [FieldDefinitionFieldTypeDto.INT, FieldDefinitionFieldTypeDto.FLOAT]);

    const getSortOptions = (table: string) =>
        getFieldOptions(table, [FieldDefinitionFieldTypeDto.DATE, FieldDefinitionFieldTypeDto.PARTIAL_DATE]);

    const [yAxisOptions, setYAxisOptions] = useState<YAxisData[] | undefined>(undefined);
    const [sortDateOptions, setSortDateOptions] = useState<SortByDate[] | undefined>(undefined);

    const defaultFormValues = useMemo<IFormData>(() => {
        const rowData =
            (panel?.triOpDateParameters?.rowData &&
                rowDataOptions?.find((r) => r.id === panel?.triOpDateParameters?.rowData)) ||
            null;

        return {
            amberMax: panel?.triOpDateParameters?.amberMaxScore ?? null,
            amberThreshold: panel?.triOpDateParameters?.amberThreshold ?? null,
            bigRedThreshold: panel?.triOpDateParameters?.bigRedThreshold ?? null,
            dataInclusionPeriod:
                (panel?.triOpDateParameters?.dataInclusionPeriod &&
                    DataInclusionPeriodOptions.find((o) => o.id === panel?.triOpDateParameters?.dataInclusionPeriod)) ||
                null,
            dataInclusionUnit:
                (panel?.triOpDateParameters?.dataInclusionUnit &&
                    DataInclusionUnits.find((o) => o.id === panel?.triOpDateParameters?.dataInclusionUnit)) ||
                null,
            dataPopulation:
                (panel?.triOpDateParameters?.dataPopulation &&
                    DataPopulationOptions.find((o) => o.id === panel.triOpDateParameters?.dataPopulation)) ||
                null,
            decision: panel?.decision ?? false,
            greenMax: panel?.triOpDateParameters?.greenMaxScore ?? null,
            includeInScoring: panel?.includeInSiteScoring ?? false,
            minRecordsSiteScore: panel?.triOpDateParameters?.minDataNeeded ?? null,
            plotTitle: panel?.name ?? '',
            redThreshold: panel?.triOpDateParameters?.redThreshold ?? null,
            rowData,
            siteAverage:
                (panel?.triOpDateParameters?.siteAverage &&
                    SiteAverageOptions.find((o) => o.id === panel?.triOpDateParameters?.siteAverage)) ||
                null,
            sortByDate:
                (rowData &&
                    panel?.triOpDateParameters?.sortField &&
                    getSortOptions(rowData.table)?.find((s) => s.id === panel?.triOpDateParameters?.sortField)) ||
                null,
            stableVariableType:
                (panel?.triOpDateParameters?.stableVariableType &&
                    StableTypeOptions.find((o) => o.id === panel?.triOpDateParameters?.stableVariableType)) ||
                null,
            stableVariableValue: panel?.triOpDateParameters?.stableValue ?? null,
            thresholdCalculation:
                (panel?.triOpDateParameters?.thresholdCalculation &&
                    ThresholdCalculationOptions.find(
                        (o) => o.id === panel?.triOpDateParameters?.thresholdCalculation,
                    )) ||
                null,
            weight: panel?.weight ?? 1,
            xAxisTitle: panel?.triOpDateParameters?.xAxisTitle ?? '',
            yAxisColumn: panel?.triOpDateParameters?.yAxisColumnValue ?? '',
            yAxisData:
                (rowData &&
                    panel?.triOpDateParameters?.yAxisDataField &&
                    getYAxisOptions(rowData.table)?.find((y) => y.id === panel?.triOpDateParameters?.yAxisDataField)) ||
                null,
            yAxisTitle: panel?.triOpDateParameters?.yAxisTitle ?? '',
            isActive: panel?.active ?? true,
            configId: panel?.configId,
        };
    }, [panel]);

    const {
        control,
        reset,
        setValue,
        handleSubmit,
        getValues,
        clearErrors,
        watch,
        trigger,
        formState: { isDirty, isValid },
    } = useForm<IFormData>({
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });

    useEffect(() => {
        const value = getValues('rowData');

        // If the form is not dirty, then this is happening the first time row data is set from its options
        // We don't want to clear the value at this point, only while the form is being edited
        if (isDirty) setValue('yAxisData', null);

        if (!value) return;

        const result = getYAxisOptions(value.table);

        setYAxisOptions(result);
    }, [watch('rowData')]);

    useEffect(() => {
        const value = getValues('rowData');

        // If the form is not dirty, then this is happening the first time row data is set from its options
        // We don't want to clear the value at this point, only while the form is being edited
        if (isDirty) setValue('sortByDate', null);

        if (!value) return;

        const result = getSortOptions(value.table);

        setSortDateOptions(result);
    }, [watch('rowData')]);

    const [isEditing, setIsEditing] = useState(!panel);
    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const submit = async (reasonForChange?: string) => {
        await handleSubmit(async (formData) => {
            try {
                if (!isValid) return;
                const savingPanel: PanelInput = {
                    id: panel?.id,
                    configId: panel?.configId,

                    organisationId: state?.organisation?.id,
                    studyId: state?.selectedStudy?.id,

                    panelTheme: panel?.panelTheme,
                    panelType: PanelType.TRI_OP_DATE,

                    name: formData.plotTitle,
                    // TODO: Once we work out how to move to the Name field exclusively, remove this duplication
                    graph: {
                        plotTitle: formData.plotTitle,
                    },

                    active: formData.isActive,
                    decision: formData.decision,
                    weight: formData.weight,
                    includeInSiteScoring: formData.includeInScoring,

                    triOpDateParameters: {
                        xAxisTitle: formData.xAxisTitle,
                        yAxisTitle: formData.yAxisTitle,
                        rowData: formData.rowData?.id,
                        amberMaxScore: formData.amberMax,
                        amberThreshold: formData.amberThreshold,
                        bigRedThreshold: formData.bigRedThreshold,
                        dataInclusionPeriod: formData.dataInclusionPeriod?.id,
                        dataInclusionUnit: formData.dataInclusionUnit?.id,
                        greenMaxScore: formData.greenMax,
                        minDataNeeded: formData.minRecordsSiteScore,
                        redThreshold: formData.redThreshold,
                        dataPopulation: formData.dataPopulation?.id,
                        sortField: formData.sortByDate?.id,
                        stableValue: formData.stableVariableValue,
                        stableVariableType: formData.stableVariableType?.id,
                        siteAverage: formData.siteAverage?.id,
                        thresholdCalculation: formData.thresholdCalculation?.id,
                        yAxisColumnValue: formData.yAxisColumn || null,
                        yAxisDataField: formData.yAxisData?.id,
                    },
                };

                const result = await savePanel({
                    variables: {
                        panel: savingPanel,
                        reason: reasonForChange ?? '',
                    },
                });

                if (result.data?.upsertPanel == null) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                ShowSuccess(tCommon('actionsSuccessfulToast'));
                setIsEditing(false);

                if (panel) {
                    onUpdate();
                } else {
                    await onCreate(result.data?.upsertPanel?.id!);
                }
            } catch (error) {
                ShowError(tCommon('errorMessage'));
                throw error;
            }
        })();
    };

    const [isStableNumber, setIsStableNumber] = useState(
        panel?.triOpDateParameters?.stableVariableType !== StableVariableType.PERCENTAGE,
    );

    useEffect(() => {
        const newState = getValues('stableVariableType')?.id !== StableVariableType.PERCENTAGE;
        setIsStableNumber(newState);
    }, [watch('stableVariableType'), panel]);

    const disableThresholds = useMemo(() => {
        return getValues('thresholdCalculation') == null;
    }, [watch('thresholdCalculation')]);

    const showStableValue = useMemo(() => {
        return getValues('stableVariableType') != null;
    }, [watch('stableVariableType')]);

    const updateChip = (tabIndex: number) => {
        if (tabIndex !== null) {
            setChip(tabIndex);
        }
    };

    // If any new field is added in the panel config that affects the panel preview, then it needs to be added here too
    // as then we need it for preview.

    const currentPanel = getValues();
    const currentConfig = {
        ...panel,
        panelType: PanelType.TRI_OP_DATE,
        graph: {
            ...panel?.graph,
            plotTitle: currentPanel?.plotTitle,
        },
        __typename: 'Panel' as 'Panel',
        triOpDateParameters: {
            amberMaxScore: currentPanel.amberMax,
            amberThreshold: currentPanel.amberThreshold,
            bigRedThreshold: currentPanel.bigRedThreshold,
            dataInclusionPeriod: currentPanel.dataInclusionPeriod?.id,
            dataInclusionUnit: currentPanel.dataInclusionUnit?.id,
            dataPopulation: currentPanel.dataPopulation?.id,
            greenMaxScore: currentPanel.greenMax,
            minDataNeeded: currentPanel.minRecordsSiteScore,
            redThreshold: currentPanel.redThreshold,
            rowData: currentPanel.rowData?.id,
            siteAverage: currentPanel.siteAverage?.id,
            sortField: currentPanel.sortByDate?.id,
            stableValue: currentPanel.stableVariableValue,
            stableVariableType: currentPanel.stableVariableType?.id,
            thresholdCalculation: currentPanel.thresholdCalculation?.id,
            yAxisColumnValue: currentPanel.yAxisColumn,
            xAxisTitle: currentPanel.xAxisTitle,
            yAxisTitle: currentPanel.yAxisTitle,
            yAxisDataField: currentPanel.yAxisData?.id,
            __typename: 'TriOpDateParameters',
        },
    };

    const content = (
        <div className="k-h-full">
            <div className="k-pt-2 k-pl-2 k-pr-2 k-mb-3">
                <Card data-testid="tri-op-date-panel-preview" className="k-min-h-min" bodyClassName="!k-p-1">
                    <ChipList
                        data-testid="panel-preview"
                        defaultData={[
                            { text: tManagePanels('configuration'), value: ChipValues.Configuration },
                            { text: tManagePanels('preview'), value: ChipValues.Preview },
                        ]}
                        value={chip}
                        onChange={(event) => {
                            updateChip(event.value as number);
                        }}
                        selection="single"
                    />
                </Card>
            </div>
            {chip === ChipValues.Configuration && (
                <div className={cn(styles.triOpsDateContainer, styles.borderTop)}>
                    <div className={cn(styles.triOpsDate)}>
                        {/* COLUMN 1 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-pr-3 k-pl-3 k-py-3 k-col-span-1 k-overflow-y-auto',
                            )}
                        >
                            <div className="k-d-flex-row k-align-items-center">
                                <div className="k-mr-4 k-min-w-fit">
                                    <CheckboxController
                                        data-testid="is-active"
                                        label={t('labels.active')}
                                        name="isActive"
                                        control={control}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className={cn(styles.borderLeft, 'k-pl-4 k-flex-grow')}>
                                    <TextBoxController
                                        label={t('labels.configId')}
                                        data-testid="config-id"
                                        name="configId"
                                        control={control}
                                        readOnly
                                        copyBtn
                                    />
                                </div>
                            </div>
                            <div>
                                <TextBoxController
                                    label={t('labels.plotTitle')}
                                    data-testid="plot-title"
                                    name="plotTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                    validateRules={{
                                        uniqueTitle: (value) => {
                                            const plotTitle = value as string;
                                            if (plotTitle != null && studyPanels?.includes(plotTitle))
                                                return t('validation.uniquePlotTitle');
                                        },
                                    }}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.xAxisTitle')}
                                    data-testid="x-axis-title"
                                    name="xAxisTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.yAxisTitle')}
                                    data-testid="y-axis-title"
                                    name="yAxisTitle"
                                    control={control}
                                    required
                                    readOnly={!isEditing}
                                    maxLength={50}
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <div className="k-d-flex-row k-justify-content-evenly k-w-full">
                                    <div className={cn(styles.borderRight, 'k-flex-1 k-pr-4 ')}>
                                        <NumericTextBoxController
                                            label={t('labels.weight')}
                                            control={control}
                                            name="weight"
                                            data-testid="weight"
                                            readOnly={!isEditing}
                                            max={10}
                                            min={1}
                                            required
                                            format="0"
                                        />
                                    </div>
                                    <div className="k-d-flex-col k-flex-1 k-pl-4 k-justify-content-center">
                                        <CheckboxController
                                            label={t('labels.includeInScoring')}
                                            control={control}
                                            name="includeInScoring"
                                            data-testid="include-in-scoring"
                                            readOnly={!isEditing}
                                        />
                                        <CheckboxController
                                            label={t('labels.decision')}
                                            control={control}
                                            name="decision"
                                            data-testid="decision"
                                            readOnly={!isEditing}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* COLUMN 2 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-gap-3 k-justify-content-start k-overflow-y-auto k-px-4 k-pb-3 k-overflow-y-auto',
                            )}
                        >
                            <div className="k-d-flex-row k-mt-3">
                                <NumericTextBoxController
                                    label={t('labels.minRecordsSiteScore')}
                                    control={control}
                                    name="minRecordsSiteScore"
                                    data-testid="min-records-site-score"
                                    required
                                    readOnly={!isEditing}
                                    min={1}
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.rowData')}
                                    control={control}
                                    name="rowData"
                                    data-testid="row-data"
                                    data={rowDataOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.yAxisData')}
                                    control={control}
                                    name="yAxisData"
                                    data-testid="y-axis-data"
                                    data={yAxisOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.sortByDate')}
                                    control={control}
                                    name="sortByDate"
                                    data-testid="sort-by-date"
                                    data={sortDateOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3">
                                <BasicDropDownController
                                    label={t('labels.siteAverage')}
                                    control={control}
                                    name="siteAverage"
                                    data-testid="site-average"
                                    data={SiteAverageOptions}
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    dataItemKey="id"
                                    textField="name"
                                />
                                <BasicDropDownController
                                    label={t('labels.dataPopulation')}
                                    control={control}
                                    name="dataPopulation"
                                    data-testid="data-population"
                                    data={DataPopulationOptions}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                />
                            </div>
                        </div>
                        {/* COLUMN 3 */}
                        <div className={cn(styles.borderRight, 'k-d-flex-col k-gap-3 k-overflow-y-auto k-px-4 k-py-3')}>
                            <Card
                                data-testid="tri-op-date-thresholds"
                                className="k-min-h-min"
                                bodyClassName="!k-pt-2"
                                title={t('thresholds')}
                            >
                                <div className="k-d-flex-col k-gap-3">
                                    <div className="k-d-flex-row">
                                        <BasicDropDownController
                                            label={t('labels.thresholdCalculation')}
                                            control={control}
                                            name="thresholdCalculation"
                                            data-testid="threshold-calculation"
                                            data={ThresholdCalculationOptions}
                                            readOnly={!isEditing}
                                            dataItemKey="id"
                                            textField="name"
                                            allowClear
                                            required
                                            onChange={async (event) => {
                                                const value = event.value as { id: string; name: string };
                                                if (value) {
                                                    if (value.id === ThresholdCalculation.MEAN_SD) {
                                                        setValue('amberThreshold', 2);
                                                        setValue('redThreshold', 3);
                                                        setValue('bigRedThreshold', 4);
                                                    }

                                                    if (value.id === ThresholdCalculation.POISSON) {
                                                        setValue('amberThreshold', 0.975);
                                                        setValue('redThreshold', 0.999);
                                                        setValue('bigRedThreshold', 0.99994);
                                                    }

                                                    if (value.id === ThresholdCalculation.STUDY_SPECIFIC) {
                                                        setValue('amberThreshold', null);
                                                        setValue('redThreshold', null);
                                                        setValue('bigRedThreshold', null);
                                                        // Trigger here to set errors for the now missing values
                                                        await trigger([
                                                            'amberThreshold',
                                                            'redThreshold',
                                                            'bigRedThreshold',
                                                        ]);
                                                        return;
                                                    }
                                                } else {
                                                    setValue('amberThreshold', null);
                                                    setValue('redThreshold', null);
                                                    setValue('bigRedThreshold', null);
                                                    // Trigger here to set errors for the now missing values
                                                    await trigger([
                                                        'amberThreshold',
                                                        'redThreshold',
                                                        'bigRedThreshold',
                                                    ]);
                                                    return;
                                                }
                                                // Clear errors for
                                                clearErrors('amberThreshold');
                                                clearErrors('redThreshold');
                                                clearErrors('bigRedThreshold');
                                            }}
                                        />
                                    </div>
                                    <div className="k-d-flex-row k-gap-3">
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                label={t('labels.amberThreshold')}
                                                control={control}
                                                name="amberThreshold"
                                                data-testid="amber-threshold"
                                                readOnly={!isEditing}
                                                min={0}
                                                validateRules={{
                                                    valueOrder: (value: number | null) => {
                                                        const redThreshold = getValues('redThreshold');
                                                        const thresholdCalculation =
                                                            getValues('thresholdCalculation')?.id;
                                                        if (
                                                            redThreshold != null &&
                                                            value != null &&
                                                            (value as number) >= redThreshold
                                                        )
                                                            return thresholdCalculation ===
                                                                ThresholdCalculation.POISSON ||
                                                                thresholdCalculation === ThresholdCalculation.MEAN_SD
                                                                ? t('validation.amberThresholdOrderGreaterThanZero')
                                                                : t('validation.amberThresholdOrder');

                                                        if (
                                                            (thresholdCalculation === ThresholdCalculation.POISSON ||
                                                                thresholdCalculation ===
                                                                    ThresholdCalculation.MEAN_SD) &&
                                                            value != null &&
                                                            (value as number) <= 0
                                                        ) {
                                                            return t('validation.amberThresholdOrderGreaterThanZero');
                                                        }

                                                        return undefined;
                                                    },
                                                }}
                                                required
                                                disabled={disableThresholds}
                                                handleInputChange={async () => {
                                                    await trigger('redThreshold');
                                                }}
                                                max={
                                                    getValues('thresholdCalculation')?.id ===
                                                    ThresholdCalculation.POISSON
                                                        ? 1
                                                        : undefined
                                                }
                                            />
                                        </div>
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                className="k-flex-1"
                                                label={t('labels.redThreshold')}
                                                control={control}
                                                name="redThreshold"
                                                data-testid="red-threshold"
                                                readOnly={!isEditing}
                                                validateRules={{
                                                    valueOrder: (value: number | null) => {
                                                        const amberThreshold = getValues('amberThreshold');
                                                        const bigRedThreshold = getValues('bigRedThreshold');
                                                        if (
                                                            amberThreshold != null &&
                                                            value != null &&
                                                            (value as number) <= amberThreshold
                                                        ) {
                                                            return t('validation.redThresholdOrder');
                                                        }
                                                        if (
                                                            bigRedThreshold != null &&
                                                            value != null &&
                                                            (value as number) >= bigRedThreshold
                                                        )
                                                            return t('validation.redThresholdOrder');

                                                        return undefined;
                                                    },
                                                }}
                                                required
                                                disabled={disableThresholds}
                                                handleInputChange={async () => {
                                                    await trigger(['amberThreshold', 'bigRedThreshold']);
                                                }}
                                                max={
                                                    getValues('thresholdCalculation')?.id ===
                                                    ThresholdCalculation.POISSON
                                                        ? 1
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="k-d-flex-row k-gap-3">
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                label={t('labels.bigRedThreshold')}
                                                control={control}
                                                name="bigRedThreshold"
                                                data-testid="big-red-threshold"
                                                readOnly={!isEditing}
                                                format="#.#####"
                                                validateRules={{
                                                    valueOrder: (value: number | null) => {
                                                        const redThreshold = getValues('redThreshold');
                                                        if (
                                                            redThreshold != null &&
                                                            value != null &&
                                                            (value as number) <= redThreshold
                                                        )
                                                            return t('validation.bigRedThresholdOrder');

                                                        return undefined;
                                                    },
                                                }}
                                                required
                                                disabled={disableThresholds}
                                                handleInputChange={async () => {
                                                    await trigger('redThreshold');
                                                }}
                                                max={
                                                    getValues('thresholdCalculation')?.id ===
                                                    ThresholdCalculation.POISSON
                                                        ? 1
                                                        : undefined
                                                }
                                            />
                                        </div>
                                        <div className="k-flex-1" />
                                    </div>
                                </div>
                            </Card>
                            <Card
                                data-testid="tri-op-date-datainclusion"
                                className="k-min-h-min"
                                title={t('dataInclusion')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-col k-gap-3">
                                    <div className="k-d-flex-row k-gap-8">
                                        <BasicDropDownController
                                            label={t('labels.dataInclusionUnit')}
                                            control={control}
                                            name="dataInclusionUnit"
                                            data-testid="data-inclusion-unit"
                                            data={DataInclusionUnits}
                                            readOnly={!isEditing}
                                            dataItemKey="id"
                                            textField="name"
                                            allowClear
                                            required
                                        />
                                        <BasicDropDownController
                                            label={t('labels.dataInclusionPeriod')}
                                            control={control}
                                            name="dataInclusionPeriod"
                                            data-testid="data-inclusion-period"
                                            data={DataInclusionPeriodOptions}
                                            readOnly={!isEditing}
                                            allowClear
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                        {/* COLUMN 4 */}
                        <div className="k-d-flex-col k-gap-3 k-overflow-y-auto k-pt-3 k-pb-1.5">
                            <div className="k-d-flex-col k-gap-3 k-pl-4 k-pr-3">
                                <div className="k-d-flex-row !k-flex-basis-1/2 k-gap-3">
                                    <div className="k-flex-1">
                                        <BasicDropDownController
                                            label={t('labels.stableType')}
                                            control={control}
                                            name="stableVariableType"
                                            data-testid="stable-variable-type"
                                            data={StableTypeOptions}
                                            readOnly={!isEditing}
                                            dataItemKey="id"
                                            textField="name"
                                            allowClear
                                            required
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        {showStableValue && (
                                            <NumericTextBoxController
                                                label={
                                                    isStableNumber
                                                        ? t('labels.stableNumber')
                                                        : t('labels.stablePercentage')
                                                }
                                                control={control}
                                                name="stableVariableValue"
                                                data-testid="stable-variable-value"
                                                readOnly={!isEditing}
                                                max={isStableNumber ? undefined : 100}
                                                min={0}
                                                required
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="k-d-flex-row !k-flex-basis-1/2 k-gap-3">
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.green')}
                                            control={control}
                                            name="greenMax"
                                            data-testid="green-max"
                                            readOnly={!isEditing}
                                            min={1}
                                            max={100}
                                            required
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const amberMax = getValues('amberMax');
                                                    if (
                                                        amberMax != null &&
                                                        value != null &&
                                                        (value as number) >= amberMax
                                                    )
                                                        return t('validation.greenMaxOrder');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger('amberMax');
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.amber')}
                                            control={control}
                                            name="amberMax"
                                            data-testid="amber-max"
                                            readOnly={!isEditing}
                                            min={1}
                                            max={100}
                                            required
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const greenMax = getValues('greenMax');
                                                    if (
                                                        greenMax != null &&
                                                        value != null &&
                                                        (value as number) <= greenMax
                                                    )
                                                        return t('validation.amberMaxOrder');

                                                    return undefined;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger('greenMax');
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="k-d-flex-row k-pl-4 k-pr-3">
                                <TextBoxController
                                    label={t('labels.yAxisColumn')}
                                    control={control}
                                    name="yAxisColumn"
                                    data-testid="y-axis-column"
                                    readOnly={!isEditing}
                                    maxLength={50}
                                />
                            </div>

                            <dl className={cn(styles.auditInfo)}>
                                <dt>{t('labels.created')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.created, 'DD-MMM-YYYY')} by{' '}
                                            {panel.createdBy?.firstName} {panel.createdBy?.lastName}
                                        </>
                                    )}
                                </dd>

                                <dt>{t('labels.updated')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.updated, 'DD-MMM-YYYY')} by{' '}
                                            {panel.updatedBy?.firstName} {panel.updatedBy?.lastName}
                                        </>
                                    )}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            )}
            {chip === ChipValues.Preview && (
                <PanelPreview
                    processedConfig={processedConfig}
                    currentConfig={currentConfig as unknown as panel_OpraQueries_panel_Panel}
                    isValidConfig={isValid}
                />
            )}
        </div>
    );

    // Show a regular card if we want to hide the edit button (on the ALL panels tab)
    const CardToUse = source === 'All' ? Card : EditCard;

    return (
        <>
            {showReasonForChange && (
                <PanelReasonForChangeDialog
                    onCancel={() => setShowReasonForChange(false)}
                    onSubmit={async (reason) => {
                        setShowReasonForChange(false);
                        await submit(reason);
                    }}
                />
            )}
            <CardToUse
                className="k-h-full"
                title={panel ? `TRI Ops-Date: ${panel.recordId}` : 'TRI Ops-Date'}
                onEdit={() => {
                    setIsEditing(true);
                }}
                padding="none"
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) return;
                    }

                    setChip(ChipValues.Configuration);
                    setIsEditing(false);
                    reset(defaultFormValues);
                    onCancelOverride?.();
                }}
                onSave={async () => {
                    if (panel && isValid) {
                        setShowReasonForChange(true);
                    } else {
                        await submit();
                    }
                }}
                isEditMode={isEditing}
                data-testid="test-id"
                saveDisabled={isSaving || !isDirty || !isValid}
                disabled={!canEdit}
            >
                {isSaving || isLoading ? <Loading /> : content}
            </CardToUse>
        </>
    );
};

const Wrapper = ({ panelRef, createData, canEdit }: IProps) => {
    const {
        loading: isLoading,
        refetch,
        data,
    } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef },
        skip: !panelRef,
        fetchPolicy: 'no-cache',
    });

    const { state } = useLocalState();

    // Getting the processed config to display the preview for previously processed configuration.

    const { data: latestDataset, isLoading: datasetLoading } = useGetLatestDataSet(state?.selectedStudy?.id);
    const latestDatasetDate = latestDataset?.created;

    const { data: processedConfig, loading: isConfigLoading } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef, timestamp: latestDatasetDate },
        skip: !panelRef || !latestDatasetDate,
        fetchPolicy: 'no-cache',
    });

    if (isLoading || isConfigLoading || datasetLoading) {
        return <Loading />;
    }

    return (
        <TRIOpDateConfig
            key={data?.panel?.auditId}
            panel={data?.panel ?? undefined}
            onCancelOverride={createData?.onCancel}
            onCreate={async (newPanelRef: string) => {
                await createData?.onCreate(newPanelRef);
            }}
            onUpdate={async () => {
                await refetch();
            }}
            canEdit={canEdit}
            processedConfig={processedConfig?.panel ?? undefined}
        />
    );
};

export default Wrapper;
